import React, {
    createContext,
    useCallback,
    useContext,
    useRef,
} from "react";

type Callback = (() => void) | null;

type FidelityNavigationGuardContextValue = {
    setOnConfirm: (cb: Callback) => void;
    setOnCancel: (cb: Callback) => void;
    runOnConfirm: () => void;
    runOnCancel: () => void;
};

const FidelityNavigationGuardContext =
    createContext<FidelityNavigationGuardContextValue | null>(null);

export function FidelityNavigationGuardProvider({
    children,
}: {
    children: React.ReactNode;
}) {
    const onConfirmRef = useRef<Callback>(null);
    const onCancelRef = useRef<Callback>(null);

    const setOnConfirm = useCallback((cb: Callback) => {
        onConfirmRef.current = cb;
    }, []);
    const setOnCancel = useCallback((cb: Callback) => {
        onCancelRef.current = cb;
    }, []);
    const runOnConfirm = useCallback(() => {
        onConfirmRef.current?.();
    }, []);
    const runOnCancel = useCallback(() => {
        onCancelRef.current?.();
    }, []);

    return (
        <FidelityNavigationGuardContext.Provider
            value={{ setOnConfirm, setOnCancel, runOnConfirm, runOnCancel }}
        >
            {children}
        </FidelityNavigationGuardContext.Provider>
    );
}

export function useFidelityNavigationGuard() {
    const ctx = useContext(FidelityNavigationGuardContext);
    if (!ctx) {
        throw new Error(
            "useFidelityNavigationGuard must be used inside a FidelityNavigationGuardProvider"
        );
    }
    return ctx;
}
